/**
 * ¿Se está generando el HTML estático en build (prerender con puppeteer)? El renderer
 * inyecta `window.__PRERENDER_INJECTED = { prerender: true }` (vite.config.ts → `inject`).
 *
 * Durante el prerender no se piden datos (las consultas se desactivan y puppeteer bloquea
 * las peticiones externas): el HTML de la portada no debe congelar estadísticas, carteleras
 * ni partidas del momento del build. Con JS, React vuelve a pintar y carga los datos vivos.
 */
export function isPrerendering(): boolean {
  if (typeof window === 'undefined') return false
  const injected = (window as Window & { __PRERENDER_INJECTED?: { prerender?: boolean } })
    .__PRERENDER_INJECTED
  return injected?.prerender === true
}

let startedFromSnapshot = false
let leftFirstPage = false

/**
 * Llamar en `main.tsx` ANTES de montar React: ¿el #root trae HTML prerenderizado (portada,
 * ayuda…) en vez del HTML base (#shell-fallback)? Ese contenido ya se está viendo.
 */
export function detectPrerenderedSnapshot(root: HTMLElement): void {
  startedFromSnapshot = root.childElementCount > 0 && !root.querySelector('#shell-fallback')
}

/**
 * La PRIMERA página sin animación de entrada (transición de página, `.reveal`):
 * - durante el prerender, para que el snapshot salga en su estado final (si no, quedaba
 *   congelado a mitad de animación con `opacity: 0`: le pasaba a /help en producción);
 * - al arrancar sobre HTML prerenderizado: ya se estaba viendo; animarlo otra vez sería un
 *   parpadeo (aparece, desaparece, entra).
 * Vale para toda la primera página (también lo que monta tarde, como una ruta lazy); al
 * navegar a otra (`markLeftFirstPage`, lo llama MainLayout) todo vuelve a animar normal.
 */
export function skipEntranceAnimation(): boolean {
  return !leftFirstPage && (isPrerendering() || startedFromSnapshot)
}

export function markLeftFirstPage(): void {
  leftFirstPage = true
}

/** Solo tests: vuelve al estado inicial del módulo. */
export function resetPrerenderStateForTests(): void {
  startedFromSnapshot = false
  leftFirstPage = false
}
